import Cookies from "js-cookie";
import { getJwtPayload } from "./jwt-utils";

const COOKIE_ACCESS = "accessToken";
const COOKIE_REFRESH = "refreshToken";
const COOKIE_GUEST = "guestToken";
const COOKIE_OLD_AUTH = "authToken";
const COOKIE_ACCOUNT_DETAILS = "accountDetails";
const COOKIE_USER_ID = "userId";
const PRIMARY_ACCOUNT_ID = "primaryAccountId";
const DEFAULT_EXPIRY_DAYS = 7;
const GUEST_EXPIRY_DAYS = 1;
const COOKIE_FRAPPE_USERNAME = process.env.REACT_APP_CRM_USERNAME_KEY;
const COOKIE_FRAPPE_PASSWORD = process.env.REACT_APP_CRM_PASSWORD_KEY;

export const cookieExpiresFromToken = (token, fallbackDays = DEFAULT_EXPIRY_DAYS) => {
  try {
    const payload = getJwtPayload(token);
    const expSec = Number(payload?.exp);
    if (Number.isFinite(expSec)) {
      return new Date(expSec * 1000);
    }
  } catch {
    // Non-JWT or invalid token; keep fallback expiry.
  }
  return fallbackDays;
};

const setCookieToParentDomain = (cookieName, cookieValue, expires = DEFAULT_EXPIRY_DAYS) => {
  const isLocalhost = window.location.hostname === "localhost";
  const config = {
    expires,
    secure: !isLocalhost,
    sameSite: "Lax",
    ...(isLocalhost ? {} : { domain: ".carrum.co.in" }),
  };
  Cookies.set(cookieName, cookieValue, config);
  return true;
};
const getCookieFromParentDomain = (cookieName) => {
  const isLocalhost = window.location.hostname === "localhost";

  const config = {
    ...(isLocalhost ? {} : { domain: ".carrum.co.in" }),
  };

  return Cookies.get(cookieName, config) || null;
};
const removeCookieFromParentDomain = (cookieName) => {
  Cookies.remove(cookieName);
  Cookies.remove(cookieName, { domain: ".carrum.co.in" });
  Cookies.remove(cookieName, { domain: ".carrum.co.in", path: "/" });
};

export const tokenManager = {
  /** Writes accessToken + authToken (same value/expiry) for legacy readers. */
  getEnv: () => {
    let env = process.env.NODE_ENV;
    if (window.location.hostname?.includes("portal")) {
      return "prod";
    } else if (window.location.hostname?.includes("qa")) {
      return "qa";
    } else {
      return "dev";
    }
  },
  setAccess: (t) => {
    if (t != null && t !== "") {
      const expires = cookieExpiresFromToken(t, DEFAULT_EXPIRY_DAYS);
      const env = tokenManager.getEnv();
      Cookies.set(COOKIE_OLD_AUTH, t, { expires });
      setCookieToParentDomain(`${env}_${COOKIE_ACCESS}`, t, expires);
    }
  },
  getAccess: () => {
    const env = tokenManager.getEnv();
    const token = getCookieFromParentDomain(`${env}_${COOKIE_ACCESS}`);
    return token || null;
  },
  removeAccess: () => {
    const env = tokenManager.getEnv();
    removeCookieFromParentDomain(`${env}_${COOKIE_ACCESS}`);
  },

  setRefresh: (t) => {
    if (t != null && t !== "") {
      const env = tokenManager.getEnv();
      const expires = cookieExpiresFromToken(t, DEFAULT_EXPIRY_DAYS);
      setCookieToParentDomain(`${env}_${COOKIE_REFRESH}`, t, expires);
    }
  },
  getRefresh: () => {
    const env = tokenManager.getEnv();
    const token = getCookieFromParentDomain(`${env}_${COOKIE_REFRESH}`);
    return token || null;
  },
  removeRefresh: () => {
    const env = tokenManager.getEnv();
    removeCookieFromParentDomain(`${env}_${COOKIE_REFRESH}`);
  },

  setAuth: (t) => {
    const env = tokenManager.getEnv();
    if (t != null && t !== "") {
      const expires = cookieExpiresFromToken(t, DEFAULT_EXPIRY_DAYS);
      Cookies.set(COOKIE_OLD_AUTH, t, { expires });
      setCookieToParentDomain(`${env}_${COOKIE_OLD_AUTH}`, t, expires);
    } else {
      Cookies.remove(COOKIE_OLD_AUTH);
      removeCookieFromParentDomain(`${env}_${COOKIE_OLD_AUTH}`);
    }
  },
  getAuth: () => {
    const env = tokenManager.getEnv();
    const token = getCookieFromParentDomain(`${env}_${COOKIE_OLD_AUTH}`);
    return token || null;
  },
  removeAuth: () => {
    const env = tokenManager.getEnv();
    removeCookieFromParentDomain(`${env}_${COOKIE_OLD_AUTH}`);
  },
  setUserId: (userId, expires = DEFAULT_EXPIRY_DAYS) => {
    if (userId != null && userId !== "") {
      const env = tokenManager.getEnv();
      setCookieToParentDomain(`${env}_${COOKIE_USER_ID}`, userId, expires);
    }
  },
  getUserId: () => {
    const env = tokenManager.getEnv();
    const userId = getCookieFromParentDomain(`${env}_${COOKIE_USER_ID}`);
    return userId || null;
  },
  removeUserId: () => {
    const env = tokenManager.getEnv();
    removeCookieFromParentDomain(`${env}_${COOKIE_USER_ID}`);
  },
  setPrimaryAccountId: (primaryAccountId, expires = DEFAULT_EXPIRY_DAYS) => {
    if (primaryAccountId != null && primaryAccountId !== "") {
      const env = tokenManager.getEnv();
      setCookieToParentDomain(`${env}_${PRIMARY_ACCOUNT_ID}`, primaryAccountId, expires);
    }
  },
  getPrimaryAccountId: () => {
    const env = tokenManager.getEnv();
    const primaryAccountId = getCookieFromParentDomain(`${env}_${PRIMARY_ACCOUNT_ID}`);
    return primaryAccountId || null;
  },
  clearPrimaryAccountId: () => {
    const env = tokenManager.getEnv();
    removeCookieFromParentDomain(`${env}_${PRIMARY_ACCOUNT_ID}`);
  },

  setAccountDetails: (encryptedPayload, expires = DEFAULT_EXPIRY_DAYS) => {
    if (!encryptedPayload) return;
    Cookies.set(COOKIE_ACCOUNT_DETAILS, encryptedPayload, { expires });
  },
  getAccountDetails: () => Cookies.get(COOKIE_ACCOUNT_DETAILS) || null,
  clearAccountDetails: () => Cookies.remove(COOKIE_ACCOUNT_DETAILS),

  setFrappeCredentials: (username, password, expires = DEFAULT_EXPIRY_DAYS) => {
    if (username != null && username !== "" && password != null && password !== "") {
      const encodedUsername = btoa(username);
      const encodedPassword = btoa(password);
      setCookieToParentDomain(`${COOKIE_FRAPPE_USERNAME}`, encodedUsername, expires);
      setCookieToParentDomain(`${COOKIE_FRAPPE_PASSWORD}`, encodedPassword, expires);
    }
  },
  getFrappeCredentials: () => {
    const encodedUsername = getCookieFromParentDomain(COOKIE_FRAPPE_USERNAME);
    const encodedPassword = getCookieFromParentDomain(COOKIE_FRAPPE_PASSWORD);

    const safeAtob = (value) => {
      if (!value || typeof value !== "string") {
        return null;
      }

      try {
        return atob(value);
      } catch {
        return null;
      }
    };

    return {
      username: safeAtob(encodedUsername),
      password: safeAtob(encodedPassword),
    };
  },
  clearFrappeCredentials: () => {
    removeCookieFromParentDomain(COOKIE_FRAPPE_USERNAME);
    removeCookieFromParentDomain(COOKIE_FRAPPE_PASSWORD);
  },

  setGuest: (t) => {
    if (t != null && t !== "") {
      Cookies.set(COOKIE_GUEST, t, {
        expires: cookieExpiresFromToken(t, GUEST_EXPIRY_DAYS),
      });
    } else {
      Cookies.remove(COOKIE_GUEST);
    }
  },
  getGuest: () => Cookies.get(COOKIE_GUEST) || null,
  clearGuest: () => Cookies.remove(COOKIE_GUEST),
  clearAll: () => {
    tokenManager.removeAccess();
    tokenManager.removeRefresh();
    tokenManager.removeAuth();
    tokenManager.removeUserId();
    tokenManager.clearPrimaryAccountId();
    tokenManager.clearAccountDetails();
    tokenManager.clearFrappeCredentials();
    tokenManager.clearGuest();
  },
};
